/**
 * Signing in.
 *
 * The guest path is first and is the widest button, because EPIC 1 story 1.3 is the one that decides
 * whether the product gets used at all: a curious user who must create an account to look around does
 * not look around. The email form is behind a toggle for the same reason — it is the slower path and
 * it should not be the first thing on screen.
 *
 * Nothing here decides whether the user is signed in; that is the router's job, from the session
 * state. This screen only performs the actions and reports their failures.
 */

import { useState } from "react";
import type { FormEvent, ReactElement } from "react";

import { copy } from "../copy.ts";
import { fieldMessagesFor, messageFor } from "../lib/errors.ts";
import { useSessionManager } from "../lib/session-context.tsx";

type Mode = "sign-in" | "register";

export function SignInScreen(): ReactElement {
  const manager = useSessionManager();
  const [mode, setMode] = useState<Mode>("sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function run(action: () => Promise<unknown>): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await action();
      // No navigation: the router switches to the app as soon as the session state has a user, so
      // navigating here would be a second source of truth for where the user is.
    } catch (caught) {
      setError(caught);
    } finally {
      setPending(false);
    }
  }

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    void run(() =>
      mode === "register"
        ? manager.registerWithPassword(email, password, displayName)
        : manager.signInWithPassword(email, password),
    );
  }

  const fieldErrors = fieldMessagesFor(error);

  return (
    <div className="sign-in">
      <h1 className="sign-in__heading">{copy.signIn.heading}</h1>
      <p className="sign-in__intro">{copy.signIn.intro}</p>

      <button
        type="button"
        className="button button--primary sign-in__guest"
        disabled={pending}
        onClick={() => void run(() => manager.signInAsGuest())}
      >
        {copy.signIn.asGuest}
      </button>
      <p className="field__help">{copy.signIn.guestEmpty}</p>

      <p className="sign-in__divider">{copy.signIn.orEmail}</p>

      <form className="form" onSubmit={onSubmit}>
        {mode === "register" && (
          <label className="field">
            <span className="field__label">{copy.signIn.displayName}</span>
            <input
              className="field__input"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              autoComplete="name"
              required
            />
          </label>
        )}

        <label className="field">
          <span className="field__label">{copy.signIn.email}</span>
          <input
            className="field__input"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            /*
              `inputMode="email"` is implied by `type="email"` on most keyboards, but not all — an
              Android keyboard without the hint shows a full layout with no `@`, which is a real
              annoyance on the device this app is built for.
            */
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            required
          />
        </label>

        <label className="field">
          <span className="field__label">{copy.signIn.password}</span>
          <input
            className="field__input"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={mode === "register" ? "new-password" : "current-password"}
            required
          />
        </label>

        {/*
          The server returns every failed rule at once, so a form shows them all rather than making the
          user fix one and resubmit. Rendered under the field the server named.
        */}
        {[...fieldErrors.entries()].map(([field, messages]) => (
          <ul key={field} className="field__errors">
            {messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        ))}

        {error !== null && fieldErrors.size === 0 && (
          <p className="form__error" role="alert">
            {messageFor(error)}
          </p>
        )}

        <button type="submit" className="button" disabled={pending}>
          {mode === "register" ? copy.signIn.register : copy.signIn.signIn}
        </button>
      </form>

      <button
        type="button"
        className="link"
        onClick={() => {
          setMode(mode === "register" ? "sign-in" : "register");
          // Cleared because the errors belonged to the other form, and leaving them would show
          // "email already taken" above a sign-in form.
          setError(null);
        }}
      >
        {mode === "register" ? copy.signIn.switchToSignIn : copy.signIn.switchToRegister}
      </button>
    </div>
  );
}
